/**
 * The Android phones card on the customer page: the customer's binding to Google, its policy templates,
 * and enrollment codes.
 *
 *   * CONNECTING OPENS GOOGLE IN A NEW TAB, opened before the request so the browser does not block it,
 *     and Google sends that tab back to this page with `?android=connected` when it is done.
 *   * ONE POLICY EDITOR, not one per row: the rows are rebuilt on every save, which would discard a
 *     half-typed Wi-Fi passphrase. Passphrases never come back from the server - a blank box keeps the
 *     stored one, as the account pages do.
 *   * THE QR CODE IS GOOGLE'S, rendered by the server: it carries the provisioning extras a
 *     factory-reset phone needs, not just the token.
 */
import { ImageControl, TextArea, type Desktop, type WebwinImage } from 'webwin';
import * as auth from '../../api/auth.js';
import { describeError } from '../../api/http.js';
import * as mobile from '../../api/mobile.js';
import {
  MobileMailClient,
  MobileOwnership,
  MobilePasscodeRequirement,
  MobileSystemUpdate,
  MobileWifiSecurity,
  type AndroidEnterpriseDto,
  type MobileEmailConfigDto,
  type MobilePolicyDto,
  type MobileWifiInput,
  type SaveMobilePolicyRequest
} from '../../contracts.gen.js';
import { join, localDate, localDateTime, plural } from '../../format.js';
import { Card } from '../../ui/Card.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h2,
  inlineRow,
  muted,
  passwordBox,
  relayout,
  splitRow,
  stackOf,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';
import { insets } from 'webwin';

export interface AndroidPhonesSection {
  card: Card;
  load(): Promise<void>;
}

/** In `MobileOwnership` order. */
export const OWNERSHIP_LABELS = [
  'Company phone, fully managed',
  'Company phone with a personal side (work profile)',
  "Employee's own phone (work profile)"
] as const;

/** In `MobilePasscodeRequirement` order. */
const PASSCODE_LABELS = [
  'No screen lock required',
  'Low - any pattern or PIN',
  'Medium - a 4-digit PIN without repeats or runs, or a password',
  'High - an 8-digit PIN without repeats or runs, or a 6-character password'
] as const;

/** In `MobileSystemUpdate` order. */
const UPDATE_LABELS = ['The phone decides', 'Install automatically', 'Install inside a daily window', 'Hold back for up to 30 days'] as const;

/** In `MobileMailClient` order. */
const MAIL_LABELS = ['No mail account', 'Outlook (Microsoft 365 or Exchange)', 'Gmail (Exchange ActiveSync)'] as const;

/** In `MobileWifiSecurity` order. */
const WIFI_SECURITY = ['Open (no password)', 'WPA / WPA2 / WPA3 personal'] as const;

interface WifiRow {
  panel: Card;
  ssid: ReturnType<typeof textBox>;
  security: ReturnType<typeof comboBox<string>>;
  passphrase: ReturnType<typeof textBox>;
  stored: boolean;
  auto: ReturnType<typeof wrappingCheck>;
  hidden: ReturnType<typeof wrappingCheck>;
}

export function androidPhonesSection(ctx: PageContext, customerId: number): AndroidPhonesSection {
  const isAdmin = auth.isAdmin();
  let enterprise: AndroidEnterpriseDto | null = null;
  let policies: MobilePolicyDto[] = [];

  // =================================================================
  //  Connection
  // =================================================================

  const connectionLine = muted('');
  const connectButton = button('Connect to Google…', () => void connect(), true);
  const disconnectButton = button('Disconnect', () => void disconnect());
  const connectionStatus = new StatusLine();
  const connectionButtons = stackOf(buttonRow(), connectButton, disconnectButton);

  // =================================================================
  //  Policies
  // =================================================================

  const policyList = new CardList<MobilePolicyDto>({
    key: p => p.id,
    version: p => `${p.updatedUtc}|${p.deviceCount}|${p.isDefault}|${p.lastPublishError ?? ''}`,
    build: buildPolicyCard
  });
  const newPolicyButton = button('New policy', () => openEditor(null));
  const policyStatus = new StatusLine();

  // ---- The editor ----
  let editing: MobilePolicyDto | null = null;
  const editorTitle = h2('New policy');
  const name = textBox({ placeholder: 'e.g. Standard, Field staff, Kiosk' });
  const isDefault = wrappingCheck('Default for new enrollments');
  const passcode = comboBox<string>(PASSCODE_LABELS, { selectedIndex: MobilePasscodeRequirement.Medium });
  const lockMinutes = textBox({ placeholder: '0 = the phone decides' });
  const wipeAfter = textBox({ placeholder: '0 = never' });
  const cameraOff = wrappingCheck('Disable the camera');
  const captureOff = wrappingCheck('Block screenshots and screen recording');
  const unknownSources = wrappingCheck('Allow apps from outside Google Play');
  const developer = wrappingCheck('Allow developer options and USB debugging');
  const usbOff = wrappingCheck('Block USB file transfer (company phones)');
  const update = comboBox<string>(UPDATE_LABELS, { selectedIndex: MobileSystemUpdate.Windowed });
  const windowStart = textBox({ placeholder: '02:00' });
  const windowEnd = textBox({ placeholder: '05:00' });
  const windowRow = fieldRow(field('Window starts', windowStart), field('Window ends', windowEnd));

  const wifiHost = vstack({ spacing: GAP.sm });
  const wifiRows: WifiRow[] = [];
  const addWifi = button('Add a Wi-Fi network', () => {
    addWifiRow(null);
    relayout(wifiHost);
  });

  const mailClient = comboBox<string>(MAIL_LABELS, { selectedIndex: MobileMailClient.None });
  const mailAddress = textBox({ placeholder: '{email}' });
  const mailUser = textBox({ placeholder: '{email}' });
  const mailServer = textBox({ placeholder: 'Blank for Microsoft 365 - e.g. mail.example.com' });
  const mailDomain = textBox({ placeholder: 'Optional - the Windows domain, e.g. CONTOSO' });
  const modernAuth = wrappingCheck('Sign in with Microsoft (modern authentication)', true);
  const useSsl = wrappingCheck('Use SSL', true);
  const signature = new TextArea({ height: 60, hAlign: 'stretch', wrap: true, placeholder: 'Optional' });
  const signatureField = field('Signature', signature);
  const mailDetails = vstack({ spacing: GAP.md });
  mailDetails.controls.addRange([
    muted(
      'Filled in per phone from its primary employee: {email} is their address, {user} the part before the @, {name} their name. No password is pushed - the person signs in the first time the app opens. The app is installed for them.'
    ),
    fieldRow(field('Email address', mailAddress), field('User name', mailUser)),
    fieldRow(field('Server', mailServer), field('Domain', mailDomain)),
    checkLine(modernAuth),
    checkLine(useSsl),
    signatureField
  ]);

  const editorStatus = new StatusLine();
  const saveButton = button('Save policy', () => void savePolicy(), true);
  const editor = card({ tint: 'alt', visible: false });
  editor.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      editorTitle,
      fieldRow(field('Name', name), field('Screen lock', passcode)),
      checkLine(isDefault),
      fieldRow(field('Lock after (minutes)', lockMinutes), field('Wipe after this many failed unlocks', wipeAfter)),
      checkLine(cameraOff),
      checkLine(captureOff),
      checkLine(unknownSources),
      checkLine(developer),
      checkLine(usbOff),
      field('System updates (company phones)', update),
      windowRow,
      value('Wi-Fi networks', { font: FONT.bold }),
      muted('Pushed to every phone on this policy. A stored passphrase is never shown - leave the box empty to keep it.'),
      wifiHost,
      stackOf(buttonRow(), addWifi),
      value('Work email', { font: FONT.bold }),
      field('Mail app', mailClient),
      mailDetails,
      stackOf(buttonRow(), saveButton, button('Cancel', () => closeEditor())),
      editorStatus.label
    )
  );

  update.on('selectionchanged', () => {
    windowRow.visible = update.selectedIndex === MobileSystemUpdate.Windowed;
    relayout(editor);
  });
  mailClient.on('selectionchanged', () => {
    applyMailVisibility();
    relayout(editor);
  });

  // =================================================================
  //  Enrollment
  // =================================================================

  const ownership = comboBox<string>(OWNERSHIP_LABELS, { selectedIndex: MobileOwnership.FullyManaged });
  const enrollPolicy = comboBox<string>([], {});
  const validDays = textBox({ text: '30' });
  const singleUse = wrappingCheck('One phone only');
  const enrollStatus = new StatusLine();
  const createCodeButton = button('Create enrollment code', () => void createCode(), true);

  const qrImage = new ImageControl({ width: 240, height: 240, sizeMode: 'fit', showBorder: false, visible: false });
  let qrTexture: WebwinImage | null = null;
  ctx.dispose.add(() => qrTexture?.destroy());
  const resultLines = vstack({ spacing: GAP.sm });
  const linkBox = textBox({ readOnly: true });
  const tokenBox = textBox({ readOnly: true });
  const resultCard = card({ tint: 'alt', visible: false });
  resultCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      qrImage,
      resultLines,
      field('Link (personal phones)', linkBox),
      field('Enrollment token', tokenBox),
      stackOf(buttonRow(), button('Open the link', () => linkBox.text && window.open(linkBox.text, '_blank', 'noopener')))
    )
  );

  const enrollPanel = vstack({ spacing: GAP.md });
  enrollPanel.controls.addRange([
    value('Enroll phones', { font: FONT.bold }),
    fieldRow(field('Phone', ownership), field('Policy', enrollPolicy)),
    fieldRow(field('Valid for (days)', validDays), checkLine(singleUse)),
    stackOf(buttonRow(), createCodeButton),
    enrollStatus.label,
    resultCard
  ]);

  // =================================================================
  //  The card
  // =================================================================

  const connectedPanel = vstack({ spacing: GAP.lg });
  connectedPanel.controls.addRange([
    stackOf(
      vstack({ spacing: GAP.sm }),
      value('Policies', { font: FONT.bold }),
      muted("What this customer's phones are set to. Each phone gets its own copy, filled in with its employee's email address."),
      policyList.panel,
      stackOf(buttonRow(), newPolicyButton),
      policyStatus.label
    ),
    editor,
    enrollPanel
  ]);

  const shell = card();
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('Android phones'),
      connectionLine,
      connectionButtons,
      connectionStatus.label,
      connectedPanel
    )
  );

  // =================================================================

  function bindEnterprise(e: AndroidEnterpriseDto): void {
    enterprise = e;
    if (e.connected) {
      connectionLine.text = join(
        [
          `Connected to Google${e.connectedUtc ? ` ${localDate(e.connectedUtc)}` : ''}${e.connectedBy ? ` by ${e.connectedBy}` : ''}`,
          plural(e.managedPhones, 'phone') + ' managed'
        ],
        ' · '
      );
      connectionLine.role = 'muted';
    } else {
      connectionLine.text = e.signupStartedUtc
        ? `Google's sign-up was opened ${localDateTime(e.signupStartedUtc)} and not finished. Connect again to start over.`
        : isAdmin
          ? "Not connected. Connecting binds this customer to its own Google enterprise - you sign up once, with any Google account or email address for this customer. Set up the Google account under Settings > Mobile devices first."
          : 'Not connected to Google yet. An administrator connects it.';
      connectionLine.role = e.signupStartedUtc ? 'warn' : 'muted';
    }
    connectButton.visible = isAdmin && !e.connected;
    disconnectButton.visible = isAdmin && e.connected && e.managedPhones === 0;
    // An empty button row still takes its line; collapse it when neither button applies.
    connectionButtons.visible = connectButton.visible || disconnectButton.visible;
    connectedPanel.visible = e.connected;
    relayout(shell);
  }

  function bindPolicies(list: MobilePolicyDto[]): void {
    policies = list;
    policyList.set(list);
    const previous = enrollPolicy.selectedIndex >= 0 ? policies[enrollPolicy.selectedIndex]?.id : undefined;
    enrollPolicy.items = list.map(p => (p.isDefault ? `${p.name} (default)` : p.name));
    const keep = previous === undefined ? -1 : list.findIndex(p => p.id === previous);
    enrollPolicy.selectedIndex = keep >= 0 ? keep : Math.max(0, list.findIndex(p => p.isDefault));
    relayout(shell);
  }

  function buildPolicyCard(p: MobilePolicyDto): Card {
    const left = vstack({ spacing: GAP.xs });
    const title = inlineRow();
    title.controls.add(value(p.name, { font: FONT.bold }));
    if (p.isDefault) title.controls.add(chip('default', 'brand'));
    left.controls.add(title);
    left.controls.add(muted(summarize(p)));
    if (p.lastPublishError) left.controls.add(muted(p.lastPublishError, 'danger'));

    const row = splitRow({ spacing: GAP.md });
    fillWith(row, left);
    const actions = buttonRow();
    actions.controls.add(button('Edit', () => openEditor(p)));
    actions.controls.add(button('Delete', () => void deletePolicy(p)));
    row.controls.add(actions);

    const shell = card({ padding: insets(12) });
    shell.controls.add(row);
    return shell;
  }

  function summarize(p: MobilePolicyDto): string {
    return join(
      [
        PASSCODE_LABELS[p.passcode]?.split(' - ')[0] + (p.passcode === MobilePasscodeRequirement.None ? '' : ' screen lock'),
        p.cameraDisabled ? 'no camera' : null,
        p.wifi.length > 0 ? plural(p.wifi.length, 'Wi-Fi network') : null,
        p.email ? `mail via ${p.email.client === MobileMailClient.Outlook ? 'Outlook' : 'Gmail'}` : null,
        plural(p.deviceCount, 'phone')
      ],
      ' · '
    );
  }

  // ---- Editor ----

  function openEditor(p: MobilePolicyDto | null): void {
    editing = p;
    editorTitle.text = p ? `Edit "${p.name}"` : 'New policy';
    name.text = p?.name ?? '';
    isDefault.checked = p?.isDefault ?? policies.length === 0;
    passcode.selectedIndex = p?.passcode ?? MobilePasscodeRequirement.Medium;
    lockMinutes.text = p ? String(Math.round(p.screenLockTimeoutSeconds / 60)) : '5';
    wipeAfter.text = p ? String(p.maxFailedUnlocksBeforeWipe) : '0';
    cameraOff.checked = p?.cameraDisabled ?? false;
    captureOff.checked = p?.screenCaptureDisabled ?? false;
    unknownSources.checked = p?.allowUnknownSources ?? false;
    developer.checked = p?.allowDeveloperSettings ?? false;
    usbOff.checked = p?.usbFileTransferDisabled ?? false;
    update.selectedIndex = p?.systemUpdate ?? MobileSystemUpdate.Windowed;
    windowStart.text = hhmm(p?.updateWindowStartMinutes ?? 120);
    windowEnd.text = hhmm(p?.updateWindowEndMinutes ?? 300);
    windowRow.visible = update.selectedIndex === MobileSystemUpdate.Windowed;

    for (const row of wifiRows.splice(0)) wifiHost.controls.remove(row.panel);
    for (const w of p?.wifi ?? []) addWifiRow(w);

    const mail: MobileEmailConfigDto | null = p?.email ?? null;
    mailClient.selectedIndex = mail?.client ?? MobileMailClient.None;
    mailAddress.text = mail?.emailAddressTemplate ?? '{email}';
    mailUser.text = mail?.userNameTemplate ?? '{email}';
    mailServer.text = mail?.serverHost ?? '';
    mailDomain.text = mail?.domain ?? '';
    modernAuth.checked = mail?.modernAuth ?? true;
    useSsl.checked = mail?.useSsl ?? true;
    signature.text = mail?.signature ?? '';
    applyMailVisibility();

    editorStatus.clear();
    editor.visible = true;
    relayout(editor);
  }

  function closeEditor(): void {
    editing = null;
    editor.visible = false;
    relayout(shell);
  }

  function applyMailVisibility(): void {
    const client = mailClient.selectedIndex as MobileMailClient;
    mailDetails.visible = client !== MobileMailClient.None;
    modernAuth.enabled = client === MobileMailClient.Outlook;
    checkLine(modernAuth).visible = client === MobileMailClient.Outlook;
    checkLine(useSsl).visible = client === MobileMailClient.GmailExchange;
    signatureField.visible = client === MobileMailClient.GmailExchange;
  }

  function addWifiRow(w: { ssid: string; security: MobileWifiSecurity; hasPassphrase: boolean; autoConnect: boolean; hidden: boolean } | null): void {
    const ssid = textBox({ text: w?.ssid ?? '', placeholder: 'Network name (SSID)' });
    const security = comboBox<string>(WIFI_SECURITY, { selectedIndex: w?.security ?? MobileWifiSecurity.Wpa });
    const passphrase = passwordBox({ placeholder: w?.hasPassphrase ? 'Stored - leave empty to keep' : 'Passphrase' });
    const auto = wrappingCheck('Connect automatically', w?.autoConnect ?? true);
    const hidden = wrappingCheck('Hidden network', w?.hidden ?? false);
    const panel = card({ padding: insets(10) });
    const row: WifiRow = { panel, ssid, security, passphrase, stored: w?.hasPassphrase ?? false, auto, hidden };
    panel.controls.add(
      stackOf(
        vstack({ spacing: GAP.sm }),
        fieldRow(field('Network', ssid), field('Security', security)),
        field('Passphrase', passphrase),
        checkLine(auto),
        checkLine(hidden),
        stackOf(
          buttonRow(),
          button('Remove network', () => {
            wifiRows.splice(wifiRows.indexOf(row), 1);
            wifiHost.controls.remove(panel);
            relayout(wifiHost);
          })
        )
      )
    );
    security.on('selectionchanged', () => {
      passphrase.enabled = security.selectedIndex === MobileWifiSecurity.Wpa;
    });
    passphrase.enabled = security.selectedIndex === MobileWifiSecurity.Wpa;
    wifiRows.push(row);
    wifiHost.controls.add(panel);
  }

  function buildPolicyRequest(): SaveMobilePolicyRequest | string {
    const policyName = name.text.trim();
    if (!policyName) return 'Give the policy a name.';
    const minutes = parseWhole(lockMinutes.text, 0);
    if (minutes === null || minutes > 1440) return 'Lock after is a number of minutes, 0 to 1440.';
    const wipe = parseWhole(wipeAfter.text, 0);
    if (wipe === null) return 'Failed unlocks before a wipe is a whole number (0 for never).';
    const start = parseHhmm(windowStart.text);
    const end = parseHhmm(windowEnd.text);
    if (update.selectedIndex === MobileSystemUpdate.Windowed && (start === null || end === null))
      return 'Write the update window as HH:MM, for example 02:00 and 05:00.';

    const wifi: MobileWifiInput[] = [];
    for (const r of wifiRows) {
      const ssid = r.ssid.text.trim();
      if (!ssid) return 'Every Wi-Fi network needs a name - remove the empty one.';
      const typed = r.passphrase.text;
      wifi.push({
        ssid,
        security: r.security.selectedIndex as MobileWifiSecurity,
        passphrase: typed.length > 0 ? typed : r.stored ? null : '',
        autoConnect: r.auto.checked,
        hidden: r.hidden.checked
      });
    }

    const client = mailClient.selectedIndex as MobileMailClient;
    const email: MobileEmailConfigDto | null =
      client === MobileMailClient.None
        ? null
        : {
            client,
            emailAddressTemplate: mailAddress.text.trim(),
            userNameTemplate: mailUser.text.trim(),
            serverHost: mailServer.text.trim(),
            domain: mailDomain.text.trim(),
            modernAuth: modernAuth.checked,
            useSsl: useSsl.checked,
            signature: signature.text
          };

    return {
      name: policyName,
      isDefault: isDefault.checked,
      passcode: passcode.selectedIndex as MobilePasscodeRequirement,
      screenLockTimeoutSeconds: minutes * 60,
      maxFailedUnlocksBeforeWipe: wipe,
      cameraDisabled: cameraOff.checked,
      screenCaptureDisabled: captureOff.checked,
      allowUnknownSources: unknownSources.checked,
      allowDeveloperSettings: developer.checked,
      usbFileTransferDisabled: usbOff.checked,
      systemUpdate: update.selectedIndex as MobileSystemUpdate,
      updateWindowStartMinutes: start ?? 120,
      updateWindowEndMinutes: end ?? 300,
      wifi,
      email
    };
  }

  async function savePolicy(): Promise<void> {
    const req = buildPolicyRequest();
    if (typeof req === 'string') {
      editorStatus.error(req);
      return;
    }
    saveButton.enabled = false;
    editorStatus.busy(editing ? 'Saving and republishing to its phones…' : 'Saving…');
    try {
      const result = editing ? await mobile.updateMobilePolicy(editing.id, req) : await mobile.createMobilePolicy(customerId, req);
      if (ctx.cancelled()) return;
      closeEditor();
      await refreshPolicies();
      const pushed = result.published > 0 ? ` Republished to ${plural(result.published, 'phone')}.` : '';
      if (result.failures.length > 0) policyStatus.error(`Saved "${result.policy.name}", but Google refused ${result.failures.length}: ${result.failures.join(' ')}`);
      else policyStatus.ok(`Saved "${result.policy.name}".${pushed}`);
    } catch (error) {
      if (!ctx.cancelled()) editorStatus.error('Could not save the policy: ' + describeError(error));
    } finally {
      saveButton.enabled = true;
    }
  }

  async function deletePolicy(p: MobilePolicyDto): Promise<void> {
    if (!(await confirm(ctx.desktop, { title: 'Delete policy', message: `Delete the policy "${p.name}"? Phones are not affected - a policy still in use cannot be deleted.` }))) return;
    try {
      await mobile.deleteMobilePolicy(p.id);
      if (ctx.cancelled()) return;
      if (editing?.id === p.id) closeEditor();
      await refreshPolicies();
      policyStatus.ok(`Deleted "${p.name}".`);
    } catch (error) {
      if (!ctx.cancelled()) policyStatus.error(describeError(error));
    }
  }

  // ---- Connection ----

  async function connect(): Promise<void> {
    // Opened inside the click, before the request: a window opened after an await has lost the
    // click's activation and is blocked as a pop-up.
    const tab = window.open('', '_blank');
    connectButton.enabled = false;
    connectionStatus.busy('Asking Google for a sign-up page…');
    try {
      const signup = await mobile.connectAndroid(customerId);
      if (ctx.cancelled()) return;
      if (tab) tab.location.href = signup.url;
      else window.location.href = signup.url;
      connectionStatus.ok("Finish the sign-up in the new tab. Google brings you back here when it is done; refresh this page if it doesn't.");
    } catch (error) {
      tab?.close();
      if (!ctx.cancelled()) connectionStatus.error('Could not start connecting: ' + describeError(error));
    } finally {
      connectButton.enabled = true;
    }
  }

  async function disconnect(): Promise<void> {
    if (
      !(await confirm(ctx.desktop, {
        title: 'Disconnect from Google',
        message: 'Forget this customer\'s Google connection? Its policies stay, and nothing changes in Google. Connecting again starts a new sign-up.'
      }))
    )
      return;
    try {
      await mobile.disconnectAndroid(customerId);
      if (ctx.cancelled()) return;
      bindEnterprise(await mobile.getAndroidEnterprise(customerId, ctx.dispose.signal()));
      connectionStatus.ok('Disconnected.');
    } catch (error) {
      if (!ctx.cancelled()) connectionStatus.error(describeError(error));
    }
  }

  // ---- Enrollment ----

  async function createCode(): Promise<void> {
    const days = parseWhole(validDays.text, 30);
    if (days === null || days < 1 || days > 90) {
      enrollStatus.error('An enrollment code is valid for 1 to 90 days.');
      return;
    }
    const policy = policies[enrollPolicy.selectedIndex];
    createCodeButton.enabled = false;
    enrollStatus.busy('Asking Google for an enrollment code…');
    try {
      const result = await mobile.createMobileEnrollment(customerId, {
        ownership: ownership.selectedIndex as MobileOwnership,
        policyId: policy?.id ?? null,
        validDays: days,
        singleUse: singleUse.checked
      });
      if (ctx.cancelled()) return;
      await showQr(ctx.desktop, result.qrPngBase64);
      linkBox.text = result.enrollmentUrl;
      tokenBox.text = result.token;
      resultLines.controls.clear();
      resultLines.controls.addRange(instructions(result.ownership).map(line => muted(line)));
      resultLines.controls.add(
        muted(`Policy "${result.policyName}" · valid until ${localDateTime(result.expiresUtc)}${result.singleUse ? ' · one phone only' : ''}. Each phone appears under Devices awaiting approval.`)
      );
      resultCard.visible = true;
      enrollStatus.clear();
      relayout(resultCard);
    } catch (error) {
      if (!ctx.cancelled()) enrollStatus.error('Could not create an enrollment code: ' + describeError(error));
    } finally {
      createCodeButton.enabled = true;
    }
  }

  async function showQr(desktop: Desktop, base64: string): Promise<void> {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const bitmap = await createImageBitmap(new Blob([bytes], { type: 'image/png' }));
    if (ctx.cancelled()) {
      bitmap.close();
      return;
    }
    // The bitmap is the texture's image and is uploaded when first drawn; the texture's destroy()
    // closes it, so it must not be closed here.
    const next = desktop.images.create(bitmap, `android-enroll-${customerId}`);
    const previous = qrTexture;
    qrTexture = next;
    qrImage.source = next;
    qrImage.visible = true;
    previous?.destroy();
  }

  async function refreshPolicies(): Promise<void> {
    const list = await mobile.getMobilePolicies(customerId, ctx.dispose.signal());
    if (!ctx.cancelled()) bindPolicies(list);
  }

  return {
    card: shell,
    async load() {
      try {
        const e = await mobile.getAndroidEnterprise(customerId, ctx.dispose.signal());
        if (ctx.cancelled()) return;
        bindEnterprise(e);
        if (ctx.query.get('android') === 'connected') connectionStatus.ok('Connected to Google. Phones can be enrolled now.');
        else if (ctx.query.get('android') === 'failed') connectionStatus.error('Google did not finish connecting this customer. See the audit log for the reason, and try again.');
        if (e.connected) await refreshPolicies();
      } catch (error) {
        if (!ctx.cancelled()) connectionStatus.error('Could not load the Android connection: ' + describeError(error));
      }
    }
  };
}

/** What to do with the code, for each way a phone can be owned. */
export function instructions(o: MobileOwnership): string[] {
  switch (o) {
    case MobileOwnership.FullyManaged:
    case MobileOwnership.CompanyOwnedWorkProfile:
      return [
        'Start from a new or factory-reset phone. On the first welcome screen, tap the same spot six times to open the QR reader, connect to Wi-Fi, and scan this code.',
        'The phone downloads Google\'s management app and sets itself up. On Android 9 and older, type afw#setup at the Google account sign-in instead and enter the token.'
      ];
    default:
      return [
        'On the employee\'s own phone, open the link below (or install Android Device Policy from Google Play and enter the token). It creates a work profile - their personal apps and data stay private.'
      ];
  }
}

function parseWhole(text: string, fallback: number): number | null {
  const t = text.trim();
  if (t === '') return fallback;
  return /^\d+$/.test(t) ? Number(t) : null;
}

/** "02:00" → 120. Null for anything that is not a time of day. */
export function parseHhmm(text: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

export function hhmm(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
